<!--
  canonical product editor LeftRail -- three sections (2026-07-15 IA):
    1. Product Setup   -- Supplier & SKU, Sizes & Colors (data entry)
    2. Placements      -- decoration list + "Add placement" wizard entry
                          (header) + "Save decoration set" (footer)
    3. Storefront      -- Pricing, Product Listing (shopper-facing)

  Plan: 02-product-editor.md (Day 1 (canonical product editor)).

  This is a DISPLAY + GROUPING structure; the LEFT_RAIL_ITEMS keys and the
  panels each nav item routes to are unchanged. Selection writes to
  useProductEditorUiState.
-->
<template>
    <nav data-testid="left-rail-root" class="left-rail-root">
        <!--
          Product-editor left-rail IA (2026-07-15, Jake). Three sections
          top-to-bottom: Product Setup (data-entry) -> Placements
          (decoration list + wizard entry) -> Storefront (shopper-facing
          presentation). This is a DISPLAY + GROUPING restructure only: the
          LEFT_RAIL_ITEMS keys and the panels each tab routes to are
          unchanged. Pricing + Storefront (relabeled "Product Listing")
          moved OUT of Product Setup into the new Storefront section; the
          Decorations section was renamed "Placements"; the "Add placement"
          affordance moved up onto the section header row and the "Save
          decoration set" button moved down below the placement list.
        -->
        <section
            data-testid="left-rail-product-setup"
            class="rail-section"
        >
            <h3 class="rail-section-header">Product Setup</h3>
            <ul class="rail-list">
                <li
                    v-for="item in productSetupItems"
                    :key="item.key"
                    class="rail-item"
                >
                    <button
                        :data-testid="`rail-item-${item.key}`"
                        :aria-current="
                            editorUi.selectedLeftRailItem === item.key
                                ? 'page'
                                : null
                        "
                        :class="{
                            'is-active':
                                editorUi.selectedLeftRailItem === item.key,
                        }"
                        type="button"
                        @click="onSelect(item.key)"
                    >
                        <span
                            class="material-icons rail-mi"
                            :data-testid="`rail-icon-${item.iconKey}`"
                            aria-hidden="true"
                        >{{ item.icon }}</span>
                        <!--
                          Two-row layout matching the app-sidebar
                          SidebarNavItem pattern (Jake 2026-07-15): the label
                          on top, a per-item sub-text line beneath it. The
                          sub-text supersedes the old inline count pills
                          (rail-sizes-count / rail-colors-count /
                          rail-supplier-sku-pill), which were removed.
                        -->
                        <span class="rail-item-text">
                            <span class="rail-item-label">{{ item.label }}</span>
                            <span
                                :data-testid="`rail-subtext-${item.iconKey}`"
                                class="rail-item-subtext"
                            >{{ itemSubtext(item.key) }}</span>
                        </span>
                    </button>
                </li>
            </ul>
        </section>

        <section
            data-testid="left-rail-decorations"
            class="rail-section"
        >
            <h3 class="rail-section-header">
                <span class="rail-section-label">Placements</span>
                <span
                    v-if="decorations.length > 0"
                    data-testid="rail-decorations-count"
                    class="rail-section-count"
                >{{ decorations.length }}</span>
                <!--
                  "Add placement" sits at the right edge of the Placements
                  section header (moved up from the old "+ Add placement"
                  list row, 2026-07-15). It opens the same
                  AddPlacementWizard the old link did -- only the button's
                  chrome changed. Restyled to a green circular FAB with a
                  white "+" glyph (Jake, LOCKED); it is icon-only, so the
                  accessible name comes from aria-label and the affordance
                  explains itself via a v-tooltip on hover. The tooltip copy
                  is single-sourced through addPlacementTooltip so the visible
                  hover text and the accessible description never drift.
                  data-testid + the click handler are unchanged -- tests and
                  the wizard wiring depend on them.
                -->
                <v-tooltip
                    location="top"
                    :text="addPlacementTooltip"
                >
                    <template #activator="{ props }">
                        <button
                            v-bind="props"
                            data-testid="rail-add-placement"
                            class="rail-section-action rail-add-fab"
                            type="button"
                            aria-label="Add placement"
                            :aria-description="addPlacementTooltip"
                            @click="openAddPlacement"
                        >
                            <span
                                class="material-icons rail-add-fab-icon"
                                aria-hidden="true"
                            >add</span>
                        </button>
                    </template>
                </v-tooltip>
            </h3>
            <ul class="rail-list">
                <li
                    v-for="(deco, i) in decorations"
                    :key="deco.id ?? deco._tempId"
                    class="rail-item"
                >
                    <template
                        v-if="
                            pendingDeleteId === (deco.id ?? deco._tempId)
                        "
                    >
                        <div
                            :data-testid="`rail-placement-${i}-confirm`"
                            class="rail-item-confirm"
                        >
                            <span class="rail-item-confirm-label"
                                >Delete this placement?</span
                            >
                            <button
                                type="button"
                                :data-testid="`rail-placement-${i}-cancel`"
                                class="rail-confirm-cancel"
                                @click="cancelDelete"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                :data-testid="`rail-placement-${i}-confirm-yes`"
                                class="rail-confirm-yes"
                                @click="
                                    confirmDelete(deco.id ?? deco._tempId)
                                "
                            >
                                Delete
                            </button>
                        </div>
                    </template>
                    <template v-else>
                        <div class="rail-placement-row">
                            <button
                                :data-testid="`rail-placement-${i}`"
                                :aria-current="
                                    isPlacementSelected(
                                        deco.id ?? deco._tempId,
                                    )
                                        ? 'page'
                                        : null
                                "
                                :class="{
                                    'is-active': isPlacementSelected(
                                        deco.id ?? deco._tempId,
                                    ),
                                }"
                                type="button"
                                class="rail-placement-select"
                                @click="
                                    onSelectPlacement(
                                        deco.id ?? deco._tempId,
                                    )
                                "
                            >
                                <span
                                    :data-testid="`rail-placement-${i}-num`"
                                    class="rail-placement-num"
                                    aria-hidden="true"
                                >{{ i + 1 }}</span>
                                <span class="rail-placement-text">
                                    <span class="rail-placement-name">{{
                                        placementLabel(deco, i)
                                    }}</span>
                                    <span
                                        v-if="placementSubline(deco)"
                                        :data-testid="`rail-placement-${i}-sub`"
                                        class="rail-placement-sub"
                                    >{{ placementSubline(deco) }}</span>
                                </span>
                            </button>
                            <!--
                              Card #545: the invalid placement's warning icon,
                              beside its row, with the exact tooltip copy (also
                              its accessible name). Focusable so a keyboard user
                              reaches the tooltip too.
                            -->
                            <v-tooltip
                                v-if="deco.id != null && isLocationUnsupported(deco)"
                                location="top"
                                :text="INVALID_PLACEMENT_TOOLTIP"
                            >
                                <template #activator="{ props: tipProps }">
                                    <span
                                        v-bind="tipProps"
                                        :data-testid="`rail-placement-${i}-invalid-icon`"
                                        class="material-icons rail-mi rail-placement-invalid-icon"
                                        role="img"
                                        tabindex="0"
                                        :aria-label="INVALID_PLACEMENT_TOOLTIP"
                                    >warning</span>
                                </template>
                            </v-tooltip>
                            <!--
                              Card #573: low-resolution icon (amber = prints
                              soft, red = below the method's minimum DPI), the
                              server's sentence as its tooltip. The verdict
                              rides on the placement row, filled by the editor
                              store from one batched read.
                            -->
                            <dpi-status-icon
                                v-if="deco.id != null && deco.dpi_resolution"
                                :resolution="deco.dpi_resolution"
                                testid="placement-dpi-icon"
                            />
                            <button
                                :data-testid="`rail-placement-${i}-delete-trigger`"
                                class="rail-placement-delete"
                                type="button"
                                aria-label="Delete placement"
                                @click="
                                    requestDelete(deco.id ?? deco._tempId)
                                "
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                            <button
                                v-if="deco.id != null"
                                :data-testid="`rail-placement-${i}-location-edit`"
                                class="rail-placement-delete rail-placement-edit"
                                type="button"
                                aria-label="Change location"
                                @click="toggleLocationEdit(deco.id)"
                            >
                                <span
                                    class="material-icons rail-mi"
                                    aria-hidden="true"
                                >edit_location</span>
                            </button>
                        </div>
                        <!--
                          A placement can sit at a location the style cannot
                          carry (after a SKU swap, card #336). It is KEPT, but
                          flagged and prompted, and the location can be changed
                          here instead of delete + re-add. The select also opens
                          on demand (the edit button) for a supported placement;
                          the placement inspector always shows it.
                        -->
                        <div
                            v-if="
                                deco.id != null
                                && (isLocationUnsupported(deco)
                                    || editingLocationId === deco.id)
                            "
                            class="rail-placement-location"
                        >
                            <p
                                v-if="isLocationUnsupported(deco)"
                                :data-testid="`rail-placement-${i}-unsupported`"
                                class="rail-placement-unsupported"
                                role="alert"
                            >
                                <span
                                    class="material-icons rail-mi"
                                    aria-hidden="true"
                                >warning</span>
                                This location isn't available on this style.
                                Choose a new location.
                            </p>
                            <PlacementLocationSelect
                                :placement="deco"
                                :testid="`rail-placement-${i}-location`"
                                @moved="onLocationMoved(deco.id)"
                            />
                        </div>
                    </template>
                </li>
            </ul>
            <!--
              "Save decoration set" lives at the BOTTOM of the Placements
              section (moved down from the header, 2026-07-15) so its label
              has room to breathe and it reads as an action ON the placement
              list above it. Disabled when 0 decorations; the component owns
              the modal + POST.
            -->
            <div class="rail-section-footer">
                <SaveAsSetButton />
            </div>
        </section>

        <section
            data-testid="left-rail-storefront"
            class="rail-section"
        >
            <h3 class="rail-section-header">Storefront</h3>
            <ul class="rail-list">
                <li
                    v-for="item in storefrontItems"
                    :key="item.key"
                    class="rail-item"
                >
                    <button
                        :data-testid="`rail-item-${item.key}`"
                        :aria-current="
                            editorUi.selectedLeftRailItem === item.key
                                ? 'page'
                                : null
                        "
                        :class="{
                            'is-active':
                                editorUi.selectedLeftRailItem === item.key,
                        }"
                        type="button"
                        @click="onSelect(item.key)"
                    >
                        <span
                            class="material-icons rail-mi"
                            :data-testid="`rail-icon-${item.iconKey}`"
                            aria-hidden="true"
                        >{{ item.icon }}</span>
                        <span class="rail-item-text">
                            <span class="rail-item-label">{{ item.label }}</span>
                            <span
                                :data-testid="`rail-subtext-${item.iconKey}`"
                                class="rail-item-subtext"
                            >{{ itemSubtext(item.key) }}</span>
                            <UnpricedSizesMarker
                                v-if="item.key === LEFT_RAIL_ITEMS.PRICING"
                                :sizes="unpricedSizes"
                                testid="rail-pricing-unpriced"
                                class="rail-unpriced"
                            />
                        </span>
                    </button>
                </li>
            </ul>
        </section>
        <!--
          P5 PR-D (Boulders #163 -- https://trello.com/c/S5kN5poU):
          the "Add placement" affordance (Placements section header,
          data-testid rail-add-placement) launches the
          AddPlacementWizard which uses the Decoration/Placement
          model via POST /api/placements/ (one-step bind shape from
          P5.5 PR-3a; was POST /api/decorations/find_or_create_and_bind/
          in P5 PR-D and retired with the SAFRS legacy surface in
          P5.5 PR-2c). The pre-P5 AddPlacementModal it replaced was
          removed in PR-Z4 dead-code sweep.
        -->
        <AddPlacementWizard
            v-model="showAddPlacementWizard"
            :campaign-product-id="campaignProductId"
            :supplier-sku="productItemNumber"
            @created="onPlacementCreated"
        />
    </nav>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { isZoneValidForSku } from "@/utils/placementZoneToViewCode";
import { useProductEditorStore } from "@/stores/productEditor";
import {
    pricingModeLabel,
    unpricedFixedSizes,
} from "@/pages/distributor/store-editor/StoreProducts/pricingMode";
import UnpricedSizesMarker from "@/pages/distributor/store-editor/StoreProducts/UnpricedSizesMarker.vue";
import { useProductEditorUiState, LEFT_RAIL_ITEMS } from "@/stores/productEditorUiState";
// P5 PR-D: AddPlacementWizard at the "+ Add placement" entry point.
// (The pre-P5 AddPlacementModal it replaced was removed in PR-Z4.)
import AddPlacementWizard from "@/components/decoration/AddPlacementWizard.vue";
import SaveAsSetButton from "@/pages/distributor/store-editor/StoreProducts/Editor/SaveAsSetButton.vue";
import PlacementLocationSelect from "@/pages/distributor/store-editor/StoreProducts/Editor/PlacementLocationSelect.vue";
import DpiStatusIcon from "@/components/decoration/DpiStatusIcon.vue";

const editor = useProductEditorStore();
// Vuex holds the shared notification toast (App.vue app-notification-toast).
const vuexStore = useStore();
const editorUi = useProductEditorUiState();

// Card #545 (Jake, 2026-10-28): exact copy of the invalid placement's
// warning-icon tooltip.
const INVALID_PLACEMENT_TOOLTIP = "This placement's location isn't valid for this SKU.";

// "+ Add placement" wizard visibility. P5 PR-D (Boulders #163)
// wires this affordance to the AddPlacementWizard which calls
// POST /api/placements/ -- the canonical writer for the
// Decoration/Placement model (P5.5 PR-3a one-step bind shape; was
// POST /api/decorations/find_or_create_and_bind/ in P5 PR-D).
const showAddPlacementWizard = ref(false);

// Pull the current campaign product id from the editor store. The
// wizard needs it to bind the new Placement to the correct product.
const campaignProductId = computed(() => editor.campaignProductId ?? null);

// Tooltip + accessible description for the green "Add placement" FAB. The
// button is icon-only, so aria-label ("Add placement") gives its short
// accessible name and this longer copy explains what the wizard does; the
// same string feeds the v-tooltip hover text so the two never drift.
const addPlacementTooltip =
    "Add a decoration placement. You can choose a decoration from the library or upload a new one.";

function openAddPlacement() {
    showAddPlacementWizard.value = true;
}

function onPlacementCreated(payload) {
    // Close the wizard as soon as the POST succeeds and announce the outcome
    // in the shared toast (the wizard has no success screen of its own: it
    // is closed before anyone could read one). Then refresh the editor's server state so
    // the new Placement shows up without a page reload.
    //
    // ``background``: a foreground refresh sets the editor's loading
    // state, and ProductEditor renders its body only while not loading,
    // so it would unmount this rail (and the wizard) and flash the page.
    // ``force``: the store throttles a reload that follows a recent one,
    // and this refresh exists because the server state just changed.
    showAddPlacementWizard.value = false;
    vuexStore.dispatch(
        "raiseSuccess",
        payload?.bound_to_existing
            ? "Bound to existing Decoration (no new approval)."
            : "Created new Decoration (queued for approval).",
    );
    if (typeof editor.loadServerState === "function") {
        editor
            .loadServerState({ force: true, background: true })
            .then(() => {
                // The wizard's POST is not a product save, so tell the
                // decorated-render triggers (Bug Backlog #356) -- once the
                // reload has put the placement in the editor, so the render
                // they start includes it.
                if (typeof editor.notePlacementSaved === "function") {
                    editor.notePlacementSaved();
                }
            })
            .catch(() => {
                // Swallow -- a failed refresh leaves the existing list
                // visible. The user can retry via the editor's own
                // refresh path.
            });
    }
}

// -----------------------------------------------------------------------
// Placement location (card #336, Jake 2026-10-15; always available since
// Jake + Mike, 2026-10-21).
//
// After a SKU swap a placement can sit at a location the new style cannot
// carry. It is KEPT (never silently dropped) but FLAGGED here, with the
// location picker (PlacementLocationSelect -- the same one the placement
// inspector always shows) open beneath it. "Cannot carry" follows the
// style's live Artifi views, the same authority the Add-placement wizard
// filters its picker by (isZoneValidForSku); with no views known nothing is
// flagged (fail open).
// -----------------------------------------------------------------------
const decorations = computed(() => editor.decorations ?? []);
const editingLocationId = ref(null);

function isLocationUnsupported(deco) {
    const slug = deco?.placement_zone_slug;
    if (!slug) return false;
    return !isZoneValidForSku(slug, editorUi.skuViews);
}

function toggleLocationEdit(placementId) {
    editingLocationId.value =
        editingLocationId.value === placementId ? null : placementId;
}

function onLocationMoved(placementId) {
    if (editingLocationId.value === placementId) editingLocationId.value = null;
}

// ADR 0033 — in-place destructive confirm.
// Only one row may be in confirm mode at a time. Clicking the delete
// trigger on a different row supersedes any open confirm.
const pendingDeleteId = ref(null);

function requestDelete(placementId) {
    pendingDeleteId.value = placementId;
}

function cancelDelete() {
    pendingDeleteId.value = null;
}

async function confirmDelete(placementId) {
    // Wired to productEditor.deletePlacement, which persists the delete
    // IMMEDIATELY (awaited) -- mirroring the sibling variant operations
    // on this surface (deleteVariant / renameVariant).
    // The optimistic UX is preserved (the row disappears), but it is now
    // backed by a real DELETE /api/placements/<id>/ rather than a delete
    // merely staged until "Save product". Server ids fire the DELETE;
    // pending (`tmp-*`) rows are dropped locally with no API call. On
    // failure the store rolls back the row and surfaces a saveError, so
    // the UI never silently no-persists.
    pendingDeleteId.value = null;
    if (typeof editor.deletePlacement === "function") {
        await editor.deletePlacement(placementId);
    }
}

// Each item carries an MD-ligature icon name (project_vuetify-icon-set-is-md-not-mdi).
// iconKey is the stable suffix for the data-testid used by tests.
// Product editor IA (2026-07-15, Jake): the left rail splits into three
// sections. Product Setup holds the data-entry steps (pick the SKU, choose
// sizes + colors). Pricing + the storefront listing moved out into the new
// Storefront section below Placements -- the shopper-facing presentation
// steps grouped together. Keys/icons/routing are unchanged; STOREFRONT is
// merely relabeled "Product Listing" for display. The `sell` icon reads as
// pricing without colliding with the store-editor Pricing section's
// `credit_card`.
const productSetupItems = [
    {
        key: LEFT_RAIL_ITEMS.SUPPLIER_SKU,
        label: "Supplier, SKU & Inventory",
        icon: "inventory_2",
        iconKey: "supplier-sku",
    },
    {
        key: LEFT_RAIL_ITEMS.SIZES_COLORS,
        label: "Sizes & Colors",
        icon: "straighten",
        iconKey: "sizes-colors",
    },
];

// Storefront section (below Placements): the shopper-facing presentation
// steps. Pricing keeps its "Pricing" label; the STOREFRONT tab is relabeled
// "Product Listing" (display only -- same key, icon, and routed panel).
const storefrontItems = [
    {
        key: LEFT_RAIL_ITEMS.PRICING,
        label: "Pricing",
        icon: "sell",
        iconKey: "pricing",
    },
    {
        key: LEFT_RAIL_ITEMS.STOREFRONT,
        label: "Product Listing",
        icon: "storefront",
        iconKey: "storefront",
    },
];


// Current SKU from the loaded campaign_product (see Stage.vue for the
// canonical path). Returns null when serverState hasn't loaded yet.
const productItemNumber = computed(
    () => editor.serverState?.data?.attributes?.product_item_number ?? null,
);

// Loading-race guard (2026-07-09): while supplier meta has not resolved the
// editor is still hydrating, so a bare "0" here reads as "this product has
// no colors" when in fact the catalog just has not arrived yet (Jake saw
// "0 colors" on a >50-color style). Show a neutral dash until meta is ready,
// then the real count -- which may legitimately be 0.
const colorsCount = computed(() => {
    if (!editor.supplierMetaReady) return "-";
    const skus = editor.getEffectiveAttribute?.("selected_color_skus");
    return Array.isArray(skus) ? skus.length : 0;
});

const sizesCount = computed(() => {
    if (!editor.supplierMetaReady) return "-";
    const sizes = editor.getEffectiveAttribute?.("selected_sizes");
    return Array.isArray(sizes) ? sizes.length : 0;
});

// -----------------------------------------------------------------------
// Product Setup rail sub-text (Jake 2026-07-15). Each rail item shows a
// sub-text line beneath its label (app-sidebar SidebarNavItem pattern).
// All sources read through the productEditor store so the sub-text stays
// live as the user edits sizes/colors, the margin, or the storefront name.
// -----------------------------------------------------------------------

// Supplier/brand name for the Supplier & SKU sub-text. Matches
// SupplierSkuPanel.vue's brandDisplay chain: the loaded SanMar meta's
// `brand` first (published to the store as supplierMeta), then the saved
// CampaignProduct `supplier_name`.
const supplierBrand = computed(
    () =>
        editor.supplierMeta?.brand ??
        editor.getEffectiveAttribute?.("supplier_name") ??
        null,
);

// "brand SKU" e.g. "SanMar PC54". Degrades gracefully: just the SKU or
// just the brand when only one is present; a neutral dash when neither is.
const supplierSkuSubtext = computed(() => {
    const parts = [supplierBrand.value, productItemNumber.value].filter(
        Boolean,
    );
    return parts.length ? parts.join(" ") : "-";
});

// "X sizes, Y colors". Keeps the supplierMetaReady dash behavior so the
// rail never flashes a misleading "0 sizes, 0 colors" during hydration
// (both counts already resolve to "-" while meta is not ready).
const sizesColorsSubtext = computed(
    () => `${sizesCount.value} sizes, ${colorsCount.value} colors`,
);

// The pricing MODE ("Fixed price" | "Store pricing", 2026-09-29). This
// replaced "Gross margin XX%", which read default_margin -- a value that no
// longer feeds the price. Neutral dash while the mode is unknown.
const pricingSubtext = computed(
    () => pricingModeLabel(editor.getEffectiveAttribute?.("pricing_mode")) ?? "-",
);

// Fixed-price sizes with no price (#3884 gate P2-2), from the LIVE
// (dirty-aware) fixed prices, so pricing a size clears the flag at once.
const unpricedSizes = computed(() =>
    unpricedFixedSizes(
        editor.getEffectiveAttribute?.("pricing_mode"),
        editor.getEffectiveAttribute?.("selected_sizes"),
        editor.getEffectiveAttribute?.("fixed_size_prices"),
    ),
);

// Storefront listing name (product_name -> Listing.Name). Neutral dash
// placeholder when unset.
const storefrontSubtext = computed(() => {
    const name = editor.getEffectiveAttribute?.("product_name");
    return name ? String(name) : "-";
});

// Resolve the sub-text for a rail item by key. One switch keeps the four
// per-item strings single-sourced from the computeds above.
function itemSubtext(itemKey) {
    switch (itemKey) {
        case LEFT_RAIL_ITEMS.SUPPLIER_SKU:
            return supplierSkuSubtext.value;
        case LEFT_RAIL_ITEMS.SIZES_COLORS:
            return sizesColorsSubtext.value;
        case LEFT_RAIL_ITEMS.PRICING:
            return pricingSubtext.value;
        case LEFT_RAIL_ITEMS.STOREFRONT:
            return storefrontSubtext.value;
        default:
            return "";
    }
}

function placementLabel(deco, idx) {
    // Fallback chain: explicit name -> placement code -> zone code ->
    // positional. The bug Day 1 closes: previously `deco?.name` was
    // skipped, so even seeded "Acme primary logo" rows rendered as
    // "LEFT_CHEST". Real names are first-class.
    //
    // Day 1 round-2 fix (smoke-caught): productEditor store carries the
    // DB column name `placement_id`, not `placement`. Both forms are
    // accepted for robustness — `placement` first wins for legacy
    // mocked rows, then `placement_id` for the real store shape.
    // `placement_id` is the placement PRIMARY KEY after the P6 PR-A rename
    // (a number, e.g. 18), NOT a human label -- never render it. The zone
    // slug (`placement_zone_slug`) is a readable fallback below name.
    return (
        deco?.name ||
        deco?.placement ||
        deco?.placement_zone_slug ||
        deco?.zone ||
        `Placement ${idx + 1}`
    );
}

// Capitalize a technique slug for display. "embroidery" -> "Embroidery",
// "screen_print" -> "Screen_print". Underscores preserved — the design
// only shows decoration_type values that don't contain underscores in
// the interim seed; if/when richer formatting is needed, swap in a more
// thorough helper.
function formatTechnique(technique) {
    if (!technique) return "";
    const s = String(technique).trim();
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// Format an imprint dimension in inches, trimming a trailing ".00"
// (4.00 -> "4", 3.50 -> "3.5") and appending the unit. Returns "" for
// null/undefined/non-numeric so the caller can omit dimensions cleanly.
function formatInches(value) {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    // Drop trailing zeros: 4.00 -> "4", 3.50 -> "3.5", 3.25 -> "3.25".
    const trimmed = String(n);
    return `${trimmed}in`;
}

const FILE_SIZE_SUFFIX = " (file size)";
// Matches the backend's rounding of the stored file size (2 decimals).
const FILE_SIZE_TOLERANCE_IN = 0.01;

// True when the placement's size IS the pinned stitch file's measured size
// (either orientation), so "(file size)" is never claimed for a size the
// user changed.
function isFileSize(deco) {
    const file = deco?.file_size_in;
    const w = Number(deco?.width_in);
    const h = Number(deco?.height_in);
    if (!file || !Number.isFinite(w) || !Number.isFinite(h)) return false;
    const near = (a, b) => Math.abs(a - b) <= FILE_SIZE_TOLERANCE_IN;
    return (
        (near(w, file.w) && near(h, file.h)) ||
        (near(w, file.h) && near(h, file.w))
    );
}

function placementSubline(deco) {
    // The zone name is the placement's LABEL (placementLabel), so the
    // subline shows technique + imprint dimensions, e.g.
    // "Embroidery - 4in x 4in". Repeating the zone here produced the
    // "Left chest Left chest . Embroidery" double-render.
    const technique = formatTechnique(deco?.decoration_type);
    const w = formatInches(deco?.width_in);
    const h = formatInches(deco?.height_in);
    const dims = w && h ? `${w} x ${h}` : "";
    if (technique && dims) {
        return `${technique} - ${dims}${isFileSize(deco) ? FILE_SIZE_SUFFIX : ""}`;
    }
    if (technique) return technique;
    if (dims) return `${dims}${isFileSize(deco) ? FILE_SIZE_SUFFIX : ""}`;
    return "";
}

function isPlacementSelected(placementId) {
    return editorUi.selectedPlacementId === placementId;
}

function onSelect(itemKey) {
    editorUi.setLeftRailItem(itemKey);
}

function onSelectPlacement(placementId) {
    editorUi.setSelectedPlacement(placementId);
}
</script>

<style scoped>
.left-rail-root {
    padding: 16px 8px;
}

.rail-section {
    margin-bottom: 24px;
}

.rail-section-header {
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.08em;
    color: #374151;
    margin: 0 12px 8px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.rail-section-label {
    flex: 0 0 auto;
}

.rail-section-action {
    margin-left: auto;
}

/* "Add placement" header-row FAB (Jake, LOCKED). A small green circular
 * button with a centered white "+" glyph, sized to sit neatly on the
 * Placements section header row where the old text button was. The header's
 * uppercase/letter-spacing is reset here since this is an icon button, not
 * the muted section title. Deep green (--tf-success-dark #2e7d32) against a
 * white icon for strong contrast; a subtle darkening + slight lift on hover. */
.rail-add-fab {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--tf-success-dark, #2e7d32);
    color: #fff;
    letter-spacing: 0;
    text-transform: none;
    cursor: pointer;
    transition:
        background-color 120ms ease-in,
        transform 120ms ease-in;
}

.rail-add-fab-icon {
    font-size: 16px;
    line-height: 1;
    color: #fff;
}

.rail-add-fab:not(:disabled):hover {
    background: #256428;
    transform: scale(1.06);
}

.rail-add-fab:focus-visible {
    outline: 2px solid var(--tf-success-dark, #2e7d32);
    outline-offset: 2px;
}

.rail-add-fab:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

/* "Save decoration set" footer, below the placement list. Left-padding
 * aligns it with the section header + list items. */
.rail-section-footer {
    margin: 8px 12px 0;
}

.rail-section-count {
    background: var(--tf-bg-subtle, #f5f8fa);
    color: var(--tf-text-muted, #5a6a82);
    border-radius: 50px;
    padding: 1px 7px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}

.rail-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.rail-item {
    margin: 0;
}

.rail-item button {
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    background: none;
    border: none;
    color: var(--tf-text-primary, #133752);
    font: inherit;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    /* Top-align the icon with the label row now that a sub-text line sits
     * below the label (two-row text column). */
    align-items: flex-start;
    gap: 9px;
}

.rail-mi {
    font-size: 17px;
    color: var(--tf-fg-4);
    flex-shrink: 0;
}

.rail-item button.is-active .rail-mi {
    color: var(--tf-brand-primary, #119ae9);
}

/* Two-row text column: label on top, sub-text beneath (app-sidebar
 * SidebarNavItem pattern). Flex column so the sub-text sits under the
 * label rather than inline with it. */
.rail-item-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.rail-item-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rail-unpriced {
    margin-top: 4px;
}

.rail-item-subtext {
    display: block;
    font-size: 13px;
    font-weight: 400;
    color: #374151;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.rail-item button.is-active .rail-item-subtext {
    color: #0a4f78;
}

.rail-item button:hover:not(:disabled) {
    background: var(--tf-bg-hover, #f3f4f6);
}

.rail-item button.is-active {
    background: var(--tf-bg-selected, #e6f3fb);
    color: var(--tf-brand-primary, #119ae9);
    font-weight: 600;
}

/* ADR 0033 — in-place destructive confirm + delete trigger. */
.rail-placement-row {
    display: flex;
    align-items: center;
}

.rail-placement-select {
    flex: 1;
    /* A flex item's automatic minimum width is its content's min-content,
     * which for the unwrapped size label is the WHOLE line. Without this the
     * select could not shrink and pushed the buttons past the rail (card
     * #609). With it the label ellipsizes and the buttons stay in the row. */
    min-width: 0;
    /* Override base rail-item button (which uses align-items: center)
     * with align-items: flex-start so the num chip + 2-line text column
     * top-align cleanly. */
    align-items: flex-start;
}

.rail-placement-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--tf-bg-subtle, #e8eff5);
    color: var(--tf-text-primary, #133752);
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 1;
}

.rail-placement-select.is-active .rail-placement-num {
    background: var(--tf-brand-primary, #119ae9);
    color: #fff;
}

.rail-placement-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.rail-placement-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rail-placement-sub {
    display: block;
    font-size: 13px;
    font-weight: 400;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 1px;
}

.rail-placement-delete {
    width: auto !important;
    padding: 4px 8px !important;
    color: var(--tf-text-muted, #6b7280);
    /* Never shrunk out of the row, and visible without hover (card #609:
     * hover-only controls are hard to find); full strength on hover/focus. */
    flex-shrink: 0;
    opacity: 0.6;
    transition: opacity 120ms ease-in;
    font-size: 16px;
    line-height: 1;
}

.rail-placement-row:hover .rail-placement-delete,
.rail-placement-delete:focus-visible {
    opacity: 1;
}

.rail-placement-delete:hover {
    color: var(--tf-color-danger, #b91c1c);
}

.rail-placement-location {
    padding: 4px 12px 8px 36px;
}

.rail-placement-unsupported {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin: 0 0 4px;
    font-size: 12px;
    color: var(--tf-color-warning-text, #92400e);
}

.rail-placement-unsupported .rail-mi {
    font-size: 16px;
}

.rail-placement-edit {
    margin-left: 0;
}

.rail-placement-invalid-icon {
    flex-shrink: 0;
    padding: 0 4px;
    font-size: 18px;
    color: var(--tf-color-warning-text, #92400e);
    cursor: help;
}

.rail-item-confirm {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: var(--tf-bg-danger-subtle, #fef2f2);
    border-radius: 6px;
}

.rail-item-confirm-label {
    flex: 1;
    font-size: 12px;
    color: var(--tf-text-primary, #133752);
}

.rail-confirm-cancel,
.rail-confirm-yes {
    width: auto !important;
    padding: 4px 8px !important;
    font-size: 12px !important;
    border-radius: 4px !important;
}

.rail-confirm-yes {
    background: var(--tf-color-danger, #b91c1c) !important;
    color: #fff !important;
}
</style>
